import { createRouter, createWebHistory } from 'vue-router';
import HomeEntry from '../views/HomeEntry.vue';
import { useAuthStore } from '../stores/authStore';
import { useTaskStore, accessDeniedMessage } from '../stores/taskStore';

const routes = [
    {
        path: '/login',
        redirect: '/'
    },
    {
        path: '/signup',
        name: 'signup',
        component: () => import('../views/Signup.vue')
    },
    {
        path: '/forgot-password',
        name: 'forgot-password',
        component: () => import('../views/ForgotPassword.vue')
    },
    {
        path: '/',
        component: HomeEntry,
        meta: { requiresAuth: false },
        children: [
            {
                path: '',
                name: 'task-dashboard',
                component: () => import('../views/UserProjects.vue'),
                meta: { requiresAuth: true }
            },
            {
                path: 'sectors',
                name: 'sectors',
                component: () => import('../views/Dashboard.vue'),
                meta: { requiresAuth: true }
            },
            {
                path: 'sectors/:sectorSlug',
                name: 'sector-projects',
                component: () => import('../views/Dashboard.vue'),
                meta: { requiresAuth: true }
            },
            {
                // The id is what actually selects the project; the slug stays in
                // the path so the URL still reads as the project's name. It is
                // optional so links bookmarked before the id existed still open —
                // those fall back to matching by slug, which is ambiguous when two
                // projects share a name, and is exactly why the id was added.
                path: 'sectors/:sectorSlug/:projectSlug/:projectId([0-9]+)?',
                name: 'taskboard',
                component: () => import('../views/TaskManagementBoard.vue'),
                meta: { requiresAuth: true }
            },
            {
                path: 'sectors/:sectorSlug/:projectSlug/analytics/:projectId([0-9]+)?',
                name: 'project-analytics',
                component: () => import('../views/BurndownVisualization.vue'),
                meta: { requiresAuth: true, permission: 'tasks.analytics.view' }
            },
            {
                // Every task and subtask of one project with its full status and
                // stage history, viewable and downloadable as Excel.
                path: 'sectors/:sectorSlug/:projectSlug/report/:projectId([0-9]+)',
                name: 'project-report',
                component: () => import('../views/ProjectReport.vue'),
                meta: { requiresAuth: true }
            },
            {
                path: 'analytics',
                name: 'analytics',
                component: () => import('../views/BurndownVisualization.vue'),
                meta: { requiresAuth: true, permission: 'tasks.analytics.view' }
            },
            {
                path: 'reporting',
                name: 'reporting',
                component: () => import('../views/Reporting.vue'),
                meta: { requiresAuth: true, permission: 'tasks.analytics.view' }
            },
            {
                // Admin-only in practice: the page checks the signed-in user and
                // shows a "restricted" panel otherwise, and every endpoint it
                // calls is gated on tasks.role.manage server-side regardless.
                path: 'roles',
                name: 'roles',
                component: () => import('../views/RoleManagement.vue'),
                meta: { requiresAuth: true }
            }
        ]
    },
    {
        path: '/:pathMatch(.*)*',
        name: 'not-found',
        component: () => import('../views/NotFound.vue')
    }
];

const router = createRouter({
    history: createWebHistory(process.env.BASE_URL),
    routes
});

// A refusal's notice is carried across its own redirect, then dropped on the
// next navigation after that.
let pendingAccessNotice = null;

router.beforeEach((to, from, next) => {
    const token = localStorage.getItem('token');
    if (token) {
        useTaskStore().accessNotice = pendingAccessNotice;
        pendingAccessNotice = null;
    }
    const requiresAuth = to.matched.some(record => record.meta.requiresAuth);

    if (requiresAuth && !token) {
        // '/' is not redirected away: HomeEntry renders the login screen there
        // when there is no token. Redirecting it would loop.
        if (to.path !== '/') {
            next({ path: '/' });
            return;
        }
    }

    if (!requiresAuth && token && (to.name === 'signup' || to.name === 'forgot-password' || to.name === 'login')) {
        next({ path: '/' });
        return;
    }

    // A route may name a permission its viewer must hold. Typing the URL by hand
    // is the case this catches — the sidebar already hides what it can. This is
    // a courtesy only: the endpoints behind these screens enforce the same
    // permission themselves, so a stale copy here cannot expose any data.
    const required = to.matched.map(r => r.meta?.permission).filter(Boolean);
    if (required.length && token) {
        const auth = useAuthStore();
        const denied = required.filter(code => !auth.can(code));
        if (denied.length) {
            // Said out loud: a silent bounce to the home page looks like a bug.
            pendingAccessNotice = accessDeniedMessage(denied[0]);
            next({ path: '/' });
            return;
        }
    }

    next();
});

router.afterEach(() => {
    document.title = 'Task Management';
});

export default router;
